import { VENDOR_TRADE_LABELS, type VendorTrade } from '@sitewise/shared';
import { Button, Input, Label } from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Mail, UserX } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  archiveVendorCompany,
  deactivateVendorTechnician,
  fetchVendorCompany,
  fetchVendorCompanyTechnicians,
  inviteVendorTechnician,
  resendVendorTechnicianInvite,
  updateVendorCompany,
} from '../../../api/vendor-companies.js';

export function VendorCompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const company = useQuery({
    queryKey: ['vendor-company', id],
    queryFn: () => fetchVendorCompany(id!),
    enabled: Boolean(id),
  });
  const techs = useQuery({
    queryKey: ['vendor-company-techs', id],
    queryFn: () => fetchVendorCompanyTechnicians(id!),
    enabled: Boolean(id),
  });

  const update = useMutation({
    mutationFn: (body: { trade?: VendorTrade; primaryContactEmail?: string | null; primaryContactPhone?: string | null }) =>
      updateVendorCompany(id!, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-company', id] }),
  });
  const archive = useMutation({
    mutationFn: () => archiveVendorCompany(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-companies'] }),
  });

  const [techName, setTechName] = useState('');
  const [techEmail, setTechEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => inviteVendorTechnician(id!, { name: techName.trim(), email: techEmail.trim() }),
    onSuccess: async () => {
      setTechName('');
      setTechEmail('');
      setError(null);
      await qc.invalidateQueries({ queryKey: ['vendor-company-techs', id] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const resend = useMutation({
    mutationFn: (userId: string) => resendVendorTechnicianInvite(id!, userId),
  });
  const deactivate = useMutation({
    mutationFn: (userId: string) => deactivateVendorTechnician(id!, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-company-techs', id] }),
  });

  if (!id) return null;

  const c = company.data?.vendorCompany;

  return (
    <div className="space-y-6">
      <Link to="/settings/vendors" className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg">
        <ArrowLeft className="h-3.5 w-3.5" /> All vendor companies
      </Link>

      <div>
        <h1 className="text-lg font-semibold text-fg">{c?.name ?? 'Vendor company'}</h1>
        {c ? (
          <p className="text-sm text-muted">
            {VENDOR_TRADE_LABELS[c.trade]}
            {c.primaryContactEmail ? ` · ${c.primaryContactEmail}` : ''}
            {c.primaryContactPhone ? ` · ${c.primaryContactPhone}` : ''}
          </p>
        ) : null}
      </div>

      {c ? (
        <section className="grid gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="trade">Trade</Label>
            <select
              id="trade"
              className="mt-1 h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
              value={c.trade}
              onChange={(e) => update.mutate({ trade: e.target.value as VendorTrade })}
            >
              {Object.entries(VENDOR_TRADE_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="email">Primary contact email</Label>
            <Input
              id="email"
              type="email"
              defaultValue={c.primaryContactEmail ?? ''}
              onBlur={(e) =>
                update.mutate({ primaryContactEmail: e.target.value.trim() || null })
              }
            />
          </div>
          <div>
            <Label htmlFor="phone">Primary contact phone</Label>
            <Input
              id="phone"
              defaultValue={c.primaryContactPhone ?? ''}
              onBlur={(e) =>
                update.mutate({ primaryContactPhone: e.target.value.trim() || null })
              }
            />
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-fg">Technicians</h2>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (!techName.trim() || !techEmail.trim()) return;
            invite.mutate();
          }}
        >
          <div className="flex-1 space-y-1">
            <Label htmlFor="tech-name">Name</Label>
            <Input id="tech-name" value={techName} onChange={(e) => setTechName(e.target.value)} required />
          </div>
          <div className="flex-1 space-y-1">
            <Label htmlFor="tech-email">Email</Label>
            <Input
              id="tech-email"
              type="email"
              value={techEmail}
              onChange={(e) => setTechEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit" disabled={invite.isPending}>
            <Mail className="h-3.5 w-3.5" /> Invite
          </Button>
        </form>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <ul className="divide-y divide-border rounded-lg border border-border">
          {(techs.data?.technicians ?? []).map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
              <div>
                <p className="font-medium text-fg">{t.name}</p>
                <p className="text-xs text-fg-muted">
                  {t.email} · {t.isActive ? 'Active' : 'Deactivated'}
                  {t.lastLoginAt ? ` · last login ${new Date(t.lastLoginAt).toLocaleDateString()}` : ' · never signed in'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => resend.mutate(t.id)}
                  disabled={resend.isPending || !t.isActive}
                >
                  Resend invite
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deactivate.mutate(t.id)}
                  disabled={deactivate.isPending || !t.isActive}
                >
                  <UserX className="h-3.5 w-3.5" /> Deactivate
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (confirm('Archive this vendor company?')) archive.mutate();
          }}
          disabled={archive.isPending}
        >
          Archive company
        </Button>
      </div>
    </div>
  );
}
